/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, ButtonProps } from '@eigtech/ui-shared-dave'
import { GroupBase } from '@eigtech/ui/components/react-select'
import {
  Controller as BaseController,
  ControllerProps,
  FieldArrayPath,
  FieldPath,
  UseControllerProps,
  UseFormProps,
  UseFormReturn,
  useController as useBaseController,
  useFormContext as useBaseFormContext,
} from 'react-hook-form'
import { z } from 'zod'
import { CheckboxField, CheckboxFieldComponent } from './components/CheckboxField'
import { CheckboxGroupField, CheckboxGroupFieldComponent } from './components/CheckboxGroupField'
import { ArrayFieldProps, ArrayField as BaseArrayField } from './components/FieldArray/ArrayField'
import { useArrayFieldContext as useBaseArrayFieldContext } from './components/FieldArray/ArrayFieldContext'
import { useArrayFieldRowContext as useBaseArrayFieldRowContext } from './components/FieldArray/ArrayFieldRowContext'
import { FieldControl, FieldControlComponent } from './components/FieldControl'
import { Form as BaseForm, FormProps } from './components/Form'
import { InputField, InputFieldComponent } from './components/InputField'
import { MaskedInputField, MaskedInputFieldComponent } from './components/MaskedInputField'
import { NumberInputField, NumberInputFieldComponent } from './components/NumberInputField'
import { RadioGroupField, RadioGroupFieldComponent } from './components/RadioGroupField'
import {
  ReactCreatableSelectField as BaseReactCreatableSelectField,
  ReactSelectField as BaseReactSelectField,
  ReactCreatableSelectFieldProps,
  ReactSelectFieldProps,
} from './components/ReactSelectField'
import { SelectField, SelectFieldComponent } from './components/SelectField'
import { SwitchField, SwitchFieldComponent } from './components/SwitchField'
import { TextareaField, TextareaFieldComponent } from './components/TextareaField'
import { useForm as useBaseForm } from './useForm'

export function createForm<T extends z.Schema<any, any>>(schema: T, id: string) {
  // Field values are the form's pre-transform input; transformed values are the
  // parsed output. zod v4 distinguishes them, so schemas with `.transform()` /
  // `.default()` produce differing input/output types (they coincide otherwise).
  type TFieldValues = z.input<T>
  type TOutput = z.output<T>

  function useForm<TContext = any, TTransformedValues = TOutput>(
    props?: UseFormProps<TFieldValues, TContext, TTransformedValues>
  ) {
    return useBaseForm<T, TContext, TTransformedValues>(schema, id, props)
  }

  function useFormContext<TTransformedValues = TOutput>() {
    return useBaseFormContext<TFieldValues, TTransformedValues>()
  }

  function useController<
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
  >(props: UseControllerProps<TFieldValues, TName, TTransformedValues>) {
    return useBaseController<TFieldValues, TName, TTransformedValues>(props)
  }

  const SubmitButton = ({
    form,
    ...props
  }: Omit<ButtonProps, 'form'> & { form: UseFormReturn<TFieldValues> }) => {
    const {
      formState: { isSubmitting },
    } = form

    return (
      <Button form={id} isLoading={isSubmitting} type="submit" {...props}>
        {props.children ?? 'Submit'}
      </Button>
    )
  }

  const Controller = <
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
  >(
    props: ControllerProps<TFieldValues, TName, TTransformedValues>
  ) => <BaseController {...props} />

  const ReactSelectField = <
    Option = unknown,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >(
    props: ReactSelectFieldProps<TFieldValues, Option, IsMulti, Group>
  ) => <BaseReactSelectField<TFieldValues, Option, IsMulti, Group> {...props} />
  const ReactCreatableSelectField = <
    Option = unknown,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >(
    props: ReactCreatableSelectFieldProps<TFieldValues, Option, IsMulti, Group>
  ) => <BaseReactCreatableSelectField<TFieldValues, Option, IsMulti, Group> {...props} />

  const ArrayField = <
    TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  >(
    props: ArrayFieldProps<TFieldValues, TFieldArrayName>
  ) => <BaseArrayField<TFieldValues, TFieldArrayName> {...props} />

  const useArrayFieldContext = <
    TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
    TKeyName extends string = 'id',
  >() => useBaseArrayFieldContext<TFieldValues, TFieldArrayName, TKeyName>()

  const useArrayFieldRowContext = <
    TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
    TKeyName extends string = 'id',
  >() => useBaseArrayFieldRowContext<TFieldValues, TFieldArrayName, TKeyName>()

  return {
    useForm,
    useFormContext,
    Form: (props: Omit<FormProps<TFieldValues, any, TOutput>, 'id'>) => (
      <BaseForm<TFieldValues, any, TOutput> id={id} {...props} />
    ),
    FormControl: FieldControl as FieldControlComponent<TFieldValues>,
    SubmitButton,

    Controller,
    useController,

    Schema: schema,

    CheckboxField: CheckboxField as CheckboxFieldComponent<TFieldValues>,
    CheckboxGroupField: CheckboxGroupField as CheckboxGroupFieldComponent<TFieldValues>,
    InputField: InputField as InputFieldComponent<TFieldValues>,
    MaskedInputField: MaskedInputField as MaskedInputFieldComponent<TFieldValues>,
    NumberInputField: NumberInputField as NumberInputFieldComponent<TFieldValues>,
    RadioGroupField: RadioGroupField as RadioGroupFieldComponent<TFieldValues>,
    ReactSelectField,
    ReactCreatableSelectField,
    SelectField: SelectField as SelectFieldComponent<TFieldValues>,
    SwitchField: SwitchField as SwitchFieldComponent<TFieldValues>,
    TextareaField: TextareaField as TextareaFieldComponent<TFieldValues>,

    ArrayField,
    useArrayFieldContext,
    useArrayFieldRowContext,
  }
}
